import { fetchAlbumList, type AlbumItem } from './album.js';
import {
	fetchTimedInspectionDevices,
	fetchTimedInspectionTimePictures,
	getTimedInspectionPictureUrl,
	type TimedInspectionDevice,
	type TimedInspectionPicture,
} from './timed-inspection.js';
import { fetchChannels, type Channel } from './device-channel.js';
import type { StoreInspectionMessageTarget } from './messages.js';
import { resolveExternalImageUrl } from '../utils/media-url.js';

type TargetPatch = Partial<StoreInspectionMessageTarget>;

export async function hydrateStoreInspectionTarget(
	baseTarget: StoreInspectionMessageTarget | null,
): Promise<StoreInspectionMessageTarget | null> {
	if (!baseTarget) return null;
	let target = finalizeTarget(baseTarget);
	try {
		const albumItem = target.picId ? await findAlbumItemByPicId(target.picId) : null;
		if (albumItem) target = finalizeTarget(mergeTarget(target, albumItemPatch(albumItem), true));
	} catch {
		// Album lookup is a best-effort read path; keep the message detail usable.
	}

	try {
		const planPicture = target.planId && target.picId
			? await findPlanPictureByPicId(target.planId, target.picId)
			: null;
		if (planPicture) target = finalizeTarget(mergeTarget(target, timedPicturePatch(planPicture), true));
	} catch {
		// Plan picture lookup only enriches Home messages when the payload is sparse.
	}

	try {
		if (target.deviceAutoId) {
			const channels = await fetchChannels(target.deviceAutoId);
			const matched = matchChannel(channels, target.channelId);
			if (matched) target = finalizeTarget(mergeTarget(target, channelPatch(matched), true));
		}
	} catch {
		// Channel details mirror the native player entry path; keep falling back to other sources if unavailable.
	}

	try {
		if (!target.deviceSn && target.storeId && target.deviceAutoId) {
			const devices = await fetchTimedInspectionDevices([target.storeId]);
			const matched = matchDevice(devices, target.deviceAutoId, target.channelId);
			if (matched) target = finalizeTarget(mergeTarget(target, devicePatch(matched), false));
		}
	} catch {
		// Missing device context should block playback, not the detail screen.
	}

	return target;
}

export async function findAlbumItemByPicId(picId: string): Promise<AlbumItem | null> {
	if (!picId) return null;
	const groups = await fetchAlbumList({ page: 1, limit: 120 });
	for (const group of groups) {
		for (const item of group.content ?? []) {
			if (String(item.pic_id ?? '') === picId) return item;
		}
	}
	return null;
}

async function findPlanPictureByPicId(planId: string, picId: string): Promise<TimedInspectionPicture | null> {
	if (!planId || !picId) return null;
	const result = await fetchTimedInspectionTimePictures(planId, '', 80);
	if (!result.ok) return null;
	return result.items.find((item) => (
		String(item.pic_id ?? '') === picId || String(item.id ?? '') === picId
	)) ?? null;
}

function albumItemPatch(item: AlbumItem): TargetPatch {
	const image = resolveExternalImageUrl(item.pic_path);
	return {
		picId: text(item.pic_id),
		storeId: text(item.store_id),
		deviceAutoId: text(item.device_auto_id),
		channelId: text(item.channel_id),
		imageUrl: image.primary,
		imageUrlRaw: image.raw,
		imageFallbackUrl: image.fallback,
		createdAt: firstTime(itemRecord(item), ['screenshot_time', 'visit_time', 'create_time']),
	};
}

function timedPicturePatch(item: TimedInspectionPicture): TargetPatch {
	const image = resolveExternalImageUrl(getTimedInspectionPictureUrl(item));
	return {
		picId: text(item.pic_id ?? item.id),
		planId: text(item.plan_id),
		storeId: text(item.store_id),
		deviceAutoId: text(item.device_auto_id),
		channelId: text(item.channel_id),
		imageUrl: image.primary,
		imageUrlRaw: image.raw,
		imageFallbackUrl: image.fallback,
		createdAt: firstTime(itemRecord(item), ['screenshot_time', 'visit_time', 'create_time']),
	};
}

function devicePatch(device: TimedInspectionDevice): TargetPatch {
	return {
		deviceAutoId: text(device.device_auto_id),
		deviceSn: text(device.device_sn ?? device.sn ?? device.serial_no ?? device.device_id),
		deviceName: text(device.alias ?? device.device_name ?? device.name ?? device.device_id ?? device.device_auto_id),
		channelId: text(device.channel_id),
		storeId: text(device.store_id),
	};
}

function channelPatch(channel: Channel): TargetPatch {
	return {
		deviceAutoId: text(channel.device_auto_id),
		deviceSn: text(channel.device_id ?? channel.device_sn ?? channel.sn ?? channel.serial_no),
		deviceName: text(channel.alias ?? channel.device_name ?? channel.name ?? channel.device_id ?? channel.device_auto_id),
		channelId: text(channel.channel_id),
		storeId: text(channel.store_id),
	};
}

function matchChannel(channels: Channel[], channelId?: string): Channel | null {
	const expected = String(channelId || '1');
	const exact = channels.find((channel) => String(channel.channel_id ?? '1') === expected);
	return exact ?? channels[0] ?? null;
}

function matchDevice(
	devices: TimedInspectionDevice[],
	deviceAutoId: string,
	channelId?: string,
): TimedInspectionDevice | null {
	const exact = devices.find((device) => (
		String(device.device_auto_id ?? '') === deviceAutoId &&
		String(device.channel_id ?? '1') === String(channelId || '1')
	));
	if (exact) return exact;
	return devices.find((device) => String(device.device_auto_id ?? '') === deviceAutoId) ?? null;
}

function mergeTarget(
	current: StoreInspectionMessageTarget,
	patch: TargetPatch,
	preferPatchMediaAndTime: boolean,
): StoreInspectionMessageTarget {
	const next: StoreInspectionMessageTarget = { ...current };
	for (const key of ['picId', 'planId', 'storeId', 'storeName', 'deviceAutoId', 'deviceSn', 'deviceName', 'channelId'] as const) {
		const value = patch[key];
		if (value && !next[key]) next[key] = value;
	}
	if (preferPatchMediaAndTime) {
		for (const key of ['imageUrl', 'imageUrlRaw', 'imageFallbackUrl', 'createdAt'] as const) {
			const value = patch[key];
			if (value) next[key] = value;
		}
	} else {
		for (const key of ['imageUrl', 'imageUrlRaw', 'imageFallbackUrl', 'createdAt'] as const) {
			const value = patch[key];
			if (value && !next[key]) next[key] = value;
		}
	}
	return next;
}

function finalizeTarget(target: StoreInspectionMessageTarget): StoreInspectionMessageTarget {
	const right = target.deviceName || target.deviceSn;
	if (target.storeName || right) {
		return { ...target, title: `${target.storeName || '--'}/${right || '--'}` };
	}
	return target;
}

function firstTime(record: Record<string, unknown>, keys: string[]): string {
	for (const key of keys) {
		const value = text(record[key]);
		if (value) return value;
	}
	return '';
}

function itemRecord(value: unknown): Record<string, unknown> {
	return value && typeof value === 'object' ? value as Record<string, unknown> : {};
}

function text(value: unknown): string {
	if (value === undefined || value === null) return '';
	return String(value).trim();
}
